"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdInfo, MdSearch } from "react-icons/md";
import "./NewOrder.css";
import { Info } from "lucide-react";

const NewOrder = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Hardcoded products
  const hardcodedProducts = [
    {
      id: "1",
      name: "Ultratech",
      category: "Cement",
      img: "/Images/ultratech.png",
    },
    {
      id: "2",
      name: "Shree",
      category: "Cement",
      img: "/Images/shree.png",
    },
    {
      id: "3",
      name: "JK Super",
      category: "Cement",
      img: "/Images/jksuper.png",
    },
    {
      id: "4",
      name: "Ambuja",
      category: "Cement",
      img: "/Images/ambuja.png",
    },
    {
      id: "5",
      name: "ACC",
      category: "Cement",
      img: "/Images/acc.png",
    },
    {
      id: "6",
      name: "JSW",
      category: "Cement",
      img: "/Images/jsw.png",
    },
    {
      id: "7",
      name: "Steel",
      category: "Steel",
      img: "/Images/steelConst.jpg",
    },
    {
      id: "8",
      name: "Construction Machinery",
      category: "Machinery",
      img: "/Images/machineryImg.jpg",
    },
  ];

  // Load products on mount
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setProducts(hardcodedProducts);
      setFilteredProducts(hardcodedProducts);
      setLoading(false);
    }, 500);
  }, []);

  // Filter products based on category and search
  useEffect(() => {
    const filtered = products.filter((product) => {
      const name = product.name?.toLowerCase() || "";
      const category = product.category?.toLowerCase() || "";
      const matchesCategory =
        selectedCategory === "all" || category === selectedCategory.toLowerCase();
      const matchesSearch = name.includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    setFilteredProducts(filtered);
  }, [products, selectedCategory, searchTerm]);

  // Generate categories dynamically from products
  const categories = [
    { id: "all", name: "All Products", count: products.length },
    ...Array.from(new Set(products.map((p) => p.category)))
      .filter(Boolean)
      .map((category) => ({
        id: category.toLowerCase(),
        name: category,
        count: products.filter(
          (p) => p.category?.toLowerCase() === category.toLowerCase()
        ).length,
      })),
  ];

  // Handle navigation based on product category
  const handleOrderNow = (product) => {
    if (product.category.toLowerCase() === "steel") {
      navigate("/dashboard/steel-order-form", { state: { product } });
    } else if (product.category.toLowerCase() === "machinery") {
      navigate("/dashboard/machinery-order-form", { state: { product } });
    } else {
      // Default: Cement or any other → use main product order form
      navigate("/dashboard/product-order-form", { state: { product } });
    }
  };

const neworderAlarm = () => {
  alert(
    "🏗️ Welcome to Premium Construction Materials\n\n" +
      "• Browse our wide range of quality products\n" +
      "• Choose your categories (Cement, Steel, Machinery)\n" +
      "• Click 'Order Now' on any product to proceed\n\n" +
      "Fast delivery • Best prices • Trusted brands"
  );
};

 if (loading) {
    return (
      <div className="new-loading-container">
        <div className="new-loader"></div>
      </div>
    );
  }

  return (
    <div className="new-order-page">
      {/* Hero Banner */}
<div className="hero-banner">
  <div className="hero-content">
    <div className="hero-badge">
      <span>🏗️ Trusted Construction Marketplace</span>
    </div>

    <h1 className="hero-title">
      Premium Construction<br />
      Materials at Best Price
    </h1>

    <p className="hero-subtitle">
      High-quality cement, steel, TMT bars, machinery &amp; more.<br />
      Delivered fast with complete trust across India.
    </p>
    {/* Trust Signals */}
    <div className="trust-bar">
      <div className="trust-item">
        <span className="trust-number">24h</span>
        <span className="trust-text">Delivery</span>
      </div>
      <div className="trust-item">
        <span className="trust-number">100%</span>
        <span className="trust-text">Quality</span>
      </div>
      <div className="trust-item">
        <span className="trust-number">50+</span>
        <span className="trust-text">Clients</span>
      </div>
    </div>
  </div>
</div>
      <div className="order-main-content">
        {/* Category Filters */}
        <div className="category-filters">
          <div className="filter-chips">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`chip ${selectedCategory === category.id ? "active" : ""}`}
              >
                {category.name}
                <span className="chip-count">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Results Header */}
        <div className="results-header">
          <h2>
            {selectedCategory === "all" ? "All Products" : categories.find(c => c.id === selectedCategory)?.name}
            <span className="result-count"> ({filteredProducts.length})</span>
             <MdInfo  size={20} style={{marginLeft:'10px',cursor:'pointer',color:'#9d9c9c'}} onClick={neworderAlarm} />
          </h2>
          
        </div>

        {/* Product Grid */}
        {filteredProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No products found</h3>
            <p>Try adjusting your search or filter</p>
          </div>
        ) : (
          <div className="product-grid">
            {filteredProducts.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-image-wrapper">
                  <img
                    src={product.img || "/placeholder.svg"}
                    alt={product.name}
                    className="product-image"
                    onError={(e) => (e.target.src = "/placeholder.svg")}
                  />
                  <span className="category-badge">{product.category}</span>
                </div>

                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  {product.description && (
                    <p className="product-desc">{product.description}</p>
                  )}
                  
                  <button
                    onClick={() => handleOrderNow(product)}
                    className="new-order-btn"
                  >
                    Order Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewOrder;